import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout/DashboardLayout";
import api from "@/lib/api";
import { adminNavItems } from "./_nav";
import { useLang } from "@/contexts/LanguageContext";
import { MapPin, Power, X, Users as UsersIcon } from "lucide-react";

export default function AdminStates() {
  const { t, lang } = useLang();
  const [states, setStates] = useState([]);
  const [stats, setStats] = useState({});
  const [loadingKey, setLoadingKey] = useState(null);

  const load = async () => {
    const [s1, s2] = await Promise.all([
      api.get("/states"),
      api.get("/admin/states/stats"),
    ]);
    setStates(s1.data);
    setStats(s2.data);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (key, current) => {
    setLoadingKey(key);
    const next = current === "live" ? "off" : "live";
    await api.patch(`/admin/states/${key}`, { status: next });
    await load();
    setLoadingKey(null);
  };

  const liveCount = states.filter(s => s.status === "live").length;
  const totalUsers = Object.values(stats).reduce((a, b) => a + b, 0);

  return (
    <DashboardLayout items={adminNavItems(t)} title="Sudan States">
      <div className="mb-6 max-w-2xl">
        <p className="text-[var(--so-text-soft)]">
          Activate or deactivate states with a single click. Only <b>live</b> states are available for customers, providers, and technicians during registration. Orders and bookings are routed to providers within the same state.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-6 so-stagger">
        <div className="so-card p-4" data-testid="states-total">
          <div className="text-xs uppercase tracking-wider text-[var(--so-text-soft)] font-bold">Total states</div>
          <div className="text-3xl font-extrabold mt-1">{states.length}</div>
        </div>
        <div className="so-card p-4" data-testid="states-live">
          <div className="text-xs uppercase tracking-wider text-[var(--so-text-soft)] font-bold">Live now</div>
          <div className="text-3xl font-extrabold mt-1 text-emerald-600">{liveCount}</div>
        </div>
        <div className="so-card p-4" data-testid="users-total">
          <div className="text-xs uppercase tracking-wider text-[var(--so-text-soft)] font-bold">Users across all states</div>
          <div className="text-3xl font-extrabold mt-1 text-[var(--so-orange)]">{totalUsers.toLocaleString()}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {states.map((s) => {
          const isLive = s.status === "live";
          const label = lang === "ar" ? s.name_ar : s.name_en;
          const userCount = stats[s.key] || 0;
          return (
            <div
              key={s.key}
              data-testid={`state-${s.key}`}
              className={`so-card p-5 transition-all ${isLive ? "border-emerald-500/40" : ""}`}
              style={isLive ? { boxShadow: "0 0 0 1px rgba(16, 185, 129, 0.25)" } : {}}
            >
              <div className="flex items-start gap-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isLive ? "bg-emerald-500/15 text-emerald-600" : "bg-[var(--so-text-soft)]/10 text-[var(--so-text-soft)]"}`}>
                  <MapPin className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-bold">{label}</div>
                    {isLive ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600">
                        <span className="w-1 h-1 rounded-full bg-emerald-500 so-pulse" /> Live
                      </span>
                    ) : (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[var(--so-text-soft)]/15 text-[var(--so-text-soft)]">Off</span>
                    )}
                  </div>
                  <div className="text-xs text-[var(--so-text-soft)] mt-0.5">{s.name_en}</div>
                  <div className="mt-2 flex items-center gap-1 text-xs text-[var(--so-text-soft)]">
                    <UsersIcon className="w-3 h-3" /> {userCount.toLocaleString()} users
                  </div>
                </div>
              </div>
              <button
                onClick={() => toggle(s.key, s.status)}
                disabled={loadingKey === s.key}
                data-testid={`toggle-${s.key}`}
                className={`mt-4 w-full justify-center inline-flex items-center gap-2 font-semibold py-2 rounded-xl text-sm transition-all disabled:opacity-50 ${
                  isLive
                    ? "border-2 border-[var(--so-border)] text-[var(--so-text-soft)] hover:border-red-500 hover:text-red-500"
                    : "bg-emerald-500 text-white hover:bg-emerald-600"
                }`}
              >
                {isLive ? <><X className="w-4 h-4" /> Disable</> : <><Power className="w-4 h-4" /> Activate</>}
              </button>
            </div>
          );
        })}
      </div>
    </DashboardLayout>
  );
}
